import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { defaultBpmByPillCountServices } from 'services/ReferenceScreenServices/defaultBpmByPillCountServices';
import {
  DefaultBpmByPillCount,
  DefaultBpmByPillCountPayload
} from 'Interfaces/ReferenceInterfaces/DefaultBpmByPillCountInterface';

export class DefaultBpmByPillCountStore {
  defaultBpmByPillCounts: DefaultBpmByPillCount[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedDefaultBpmByPillCount: DefaultBpmByPillCount | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  // Initial load method (with loading indicator)
  fetchDefaultBpmByPillCounts = async (page = 1, pageSize = 100) => {
    this.isLoading = true;
    try {
      const response = await defaultBpmByPillCountServices.getAllDefaultBpmByPillCounts(
        page,
        pageSize
      );
      runInAction(() => {
        this.defaultBpmByPillCounts = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching default BPM by pill counts');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Polling method (without loading indicator)
  pollDefaultBpmByPillCounts = async (page = 1, pageSize = 100) => {
    this.isPolling = true;
    try {
      const response = await defaultBpmByPillCountServices.getAllDefaultBpmByPillCounts(
        page,
        pageSize
      );
      runInAction(() => {
        // Update data only if it has changed
        if (JSON.stringify(this.defaultBpmByPillCounts) !== JSON.stringify(response.data)) {
          this.defaultBpmByPillCounts = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
        }
      });
    } catch (error) {
      // Don't show toast for polling errors to avoid spam
      if(this.environment === 'development') {
        console.error('Error polling default BPM by pill counts:', error);
      }
      toast.error('Error polling default BPM by pill counts!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async (page = 1, pageSize = 100) => {
    await this.fetchDefaultBpmByPillCounts(page, pageSize);
  };

  createDefaultBpmByPillCount = async (
    defaultBpmByPillCountData: DefaultBpmByPillCountPayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response =
        await defaultBpmByPillCountServices.createDefaultBpmByPillCount(defaultBpmByPillCountData);
      if (response.status) {
        toast.success('Default BPM by pill count created successfully');
        await this.fetchDefaultBpmByPillCounts(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to create default BPM by pill count');
        return false;
      }
    } catch (error) {
      toast.error('Error creating default BPM by pill count');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateDefaultBpmByPillCount = async (
    id: number,
    defaultBpmByPillCountData: DefaultBpmByPillCountPayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await defaultBpmByPillCountServices.updateDefaultBpmByPillCount(
        id,
        defaultBpmByPillCountData
      );
      if (response.status) {
        toast.success('Default BPM by pill count updated successfully');
        await this.fetchDefaultBpmByPillCounts(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to update default BPM by pill count');
        return false;
      }
    } catch (error) {
      toast.error('Error updating default BPM by pill count');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteDefaultBpmByPillCount = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await defaultBpmByPillCountServices.deleteDefaultBpmByPillCount(id);
      if (response.status) {
        toast.success('Default BPM by pill count deleted successfully');

        const isLastItemOnPage = this.defaultBpmByPillCounts.length === 1;
        const isLastPage = this.pagination.currentPage === this.pagination.totalPages;

        if (isLastItemOnPage && !isLastPage && this.pagination.currentPage > 1) {
          await this.fetchDefaultBpmByPillCounts(this.pagination.currentPage - 1);
        } else {
          await this.fetchDefaultBpmByPillCounts(this.pagination.currentPage);
        }
      } else {
        toast.error(response.message || 'Failed to delete default BPM by pill count');
      }
    } catch (error) {
      toast.error('Error deleting default BPM by pill count');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedDefaultBpmByPillCount = (defaultBpmByPillCount: DefaultBpmByPillCount | null) => {
    this.selectedDefaultBpmByPillCount = defaultBpmByPillCount;
  };
}

export const defaultBpmByPillCountStore = new DefaultBpmByPillCountStore();
